import React, { useState, useEffect } from 'react';
import {
  Activity, IndianRupee, LifeBuoy, ReceiptText, Loader,
  TrendingUp, AlertTriangle, Users, Plug, CheckCircle, XCircle, Clock,
} from 'lucide-react';
import {
  getMisOperations, getMisFinancial, getMisSupport, getMisAccounting,
} from '../services/api';

const TABS = [
  { key: 'operations', label: 'Operations', icon: Activity,    loader: getMisOperations },
  { key: 'financial',  label: 'Financial',  icon: IndianRupee, loader: getMisFinancial  },
  { key: 'support',    label: 'Support',    icon: LifeBuoy,    loader: getMisSupport    },
  { key: 'accounting', label: 'Accounting', icon: ReceiptText, loader: getMisAccounting },
];

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const num = (n) => Number(n || 0).toLocaleString('en-IN');

function KPI({ icon: Icon, label, value, sub, tone = 'default' }) {
  const ring = tone === 'warn' ? 'border-amber-500/30' : tone === 'bad' ? 'border-red-500/30' : 'border-ink-700';
  return (
    <div className={`bg-ink-800 border ${ring} rounded-xl p-4`}>
      <div className="flex items-center gap-2 text-ink-400 text-xs uppercase tracking-wide">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <p className="mt-1 text-2xl font-bold text-ink-100">{value}</p>
      {sub != null && <p className="text-xs text-ink-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function SparkBars({ data, dateKey = 'date', valueKey = 'count', height = 56 }) {
  const max = Math.max(1, ...data.map((d) => d[valueKey] || 0));
  return (
    <div className="flex items-end gap-1" style={{ height }}>
      {data.map((d) => (
        <div key={d[dateKey]} title={`${d[dateKey]}: ${d[valueKey]}`}
          className="flex-1 bg-brand-500/70 hover:bg-brand-500 rounded-sm transition-colors"
          style={{ height: `${Math.max(2, (d[valueKey] / max) * height)}px` }} />
      ))}
    </div>
  );
}

/* ── Operations ─────────────────────────────────────────────────────────── */
function Operations({ data }) {
  const s = data.summary;
  return (
    <div className="space-y-6">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <KPI icon={Activity}  label="Active right now" value={num(s.active)} sub={`${s.panel_interviews_active} panel`} />
        <KPI icon={Clock}     label="Started today"    value={num(s.today)} />
        <KPI icon={TrendingUp} label="Last 7 days"     value={num(s.week)}  sub={`${s.completed_week} completed · ${s.abandoned_week} abandoned`} />
        <KPI icon={Users}     label="Last 30 days"     value={num(s.month)} sub={`${s.pending_invites} pending · ${s.expired_invites} expired`} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="bg-ink-800 border border-ink-700 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wide text-ink-400 mb-3">Last 14 days · daily sessions</p>
          {data.daily_volume?.length ? <SparkBars data={data.daily_volume} /> : <p className="text-sm text-ink-500">No activity yet.</p>}
        </div>
        <div className="bg-ink-800 border border-ink-700 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wide text-ink-400 mb-3">Status mix (last 30 days)</p>
          <div className="space-y-2">
            {Object.entries(data.status_mix || {}).map(([k, v]) => {
              const total = Object.values(data.status_mix).reduce((a, b) => a + b, 0) || 1;
              const pct = Math.round((v / total) * 100);
              return (
                <div key={k}>
                  <div className="flex justify-between text-xs text-ink-300"><span className="capitalize">{k}</span><span>{v} · {pct}%</span></div>
                  <div className="h-1.5 bg-ink-700 rounded-full overflow-hidden"><div className="h-full bg-brand-500" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
            {Object.keys(data.status_mix || {}).length === 0 && <p className="text-sm text-ink-500">No sessions yet.</p>}
          </div>
        </div>
      </div>

      <div className="bg-ink-800 border border-ink-700 rounded-xl p-4">
        <p className="text-xs uppercase tracking-wide text-ink-400 mb-3">Top 5 organizations · last 30 days</p>
        {data.top_orgs?.length ? (
          <table className="w-full text-sm">
            <thead><tr className="text-ink-400 text-xs"><th className="text-left py-1">Organization</th><th className="text-right py-1">Interviews</th></tr></thead>
            <tbody>{data.top_orgs.map((o) => (<tr key={o.org_id} className="border-t border-ink-700"><td className="py-2 text-ink-200">{o.name}</td><td className="py-2 text-right text-ink-100 font-medium">{num(o.count)}</td></tr>))}</tbody>
          </table>
        ) : <p className="text-sm text-ink-500">No data.</p>}
      </div>
    </div>
  );
}

/* ── Financial ─────────────────────────────────────────────────────────── */
function Financial({ data }) {
  const s = data.summary;
  return (
    <div className="space-y-6">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <KPI icon={IndianRupee} label="Revenue (lifetime)" value={inr(s.revenue_total_inr)} sub={`${s.transactions} transactions`} />
        <KPI icon={TrendingUp}  label="Revenue (last 30d)" value={inr(s.revenue_month_inr)} sub={`year ${inr(s.revenue_year_inr)}`} />
        <KPI icon={Users}       label="Credits sold"       value={num(s.credits_sold)} sub={`${num(s.credits_granted)} granted`} />
        <KPI icon={AlertTriangle} label="Outstanding credits" value={num(s.credits_outstanding)} sub={`${num(s.credits_consumed)} consumed`}
             tone={s.credits_outstanding > 0 ? 'warn' : 'default'} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="bg-ink-800 border border-ink-700 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wide text-ink-400 mb-3">Monthly revenue (last 12 months)</p>
          {data.monthly_revenue?.length ? (
            <SparkBars data={data.monthly_revenue} dateKey="month" valueKey="inr" height={80} />
          ) : <p className="text-sm text-ink-500">No revenue yet.</p>}
        </div>
        <div className="bg-ink-800 border border-ink-700 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wide text-ink-400 mb-3">Top 5 organizations by revenue</p>
          {data.top_orgs_by_revenue?.length ? (
            <table className="w-full text-sm">
              <thead><tr className="text-ink-400 text-xs"><th className="text-left py-1">Organization</th><th className="text-right py-1">Revenue</th></tr></thead>
              <tbody>{data.top_orgs_by_revenue.map((o) => (<tr key={o.org_id} className="border-t border-ink-700"><td className="py-2 text-ink-200">{o.name}</td><td className="py-2 text-right text-ink-100 font-medium">{inr(o.inr)}</td></tr>))}</tbody>
            </table>
          ) : <p className="text-sm text-ink-500">No revenue yet.</p>}
        </div>
      </div>
    </div>
  );
}

/* ── Support ───────────────────────────────────────────────────────────── */
function Support({ data }) {
  const s = data.summary;
  return (
    <div className="space-y-6">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <KPI icon={Activity} label="Active sessions" value={num(s.active_sessions)} />
        <KPI icon={AlertTriangle} label="Abandoned (7d)" value={num(s.abandoned_week)} tone={s.abandoned_week > 0 ? 'warn' : 'default'} />
        <KPI icon={Plug}     label="Integrations" value={num(s.total_integrations)} sub={`${s.sync_enabled} auto-sync`} />
        <KPI icon={AlertTriangle} label="Failed / stale"
             value={`${num(s.failed_integrations)} / ${num(s.stale_integrations)}`}
             sub="failed syncs · stale (>24h)"
             tone={(s.failed_integrations + s.stale_integrations) > 0 ? 'bad' : 'default'} />
      </div>

      <div className="bg-ink-800 border border-ink-700 rounded-xl p-4">
        <p className="text-xs uppercase tracking-wide text-ink-400 mb-3 flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" /> Failed integration syncs</p>
        {data.failed_syncs?.length ? (
          <table className="w-full text-sm">
            <thead><tr className="text-ink-400 text-xs"><th className="text-left py-1">Connection</th><th className="text-left py-1">Org</th><th className="text-left py-1">Error</th><th className="text-right py-1">Last try</th></tr></thead>
            <tbody>{data.failed_syncs.map((f) => (
              <tr key={f.integration_id} className="border-t border-ink-700">
                <td className="py-2 text-ink-200">{f.name}<span className="text-ink-500 text-xs"> · {f.type}</span></td>
                <td className="py-2 text-ink-400">{f.organization_id}</td>
                <td className="py-2 text-red-300 text-xs">{f.last_sync_error?.slice(0, 80)}</td>
                <td className="py-2 text-right text-ink-500 text-xs">{f.last_sync_at ? new Date(f.last_sync_at).toLocaleString() : '—'}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : <p className="text-sm text-ink-500 flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-green-400" /> No integration failures.</p>}
      </div>

      <div className="bg-ink-800 border border-ink-700 rounded-xl p-4">
        <p className="text-xs uppercase tracking-wide text-ink-400 mb-3">Recent platform errors</p>
        {data.recent_errors?.length ? (
          <ul className="space-y-1.5 text-xs">
            {data.recent_errors.map((e, i) => (
              <li key={i} className="text-ink-300 border-b border-ink-700/60 pb-1.5">
                <span className="text-red-300 mr-2">{e.event_type || 'error'}</span>
                <span className="text-ink-400">{e.message}</span>
                <span className="text-ink-500 ml-2">· {e.session_id || ''}</span>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-ink-500 flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-green-400" /> No errors in the recent window.</p>}
      </div>
    </div>
  );
}

/* ── Accounting ────────────────────────────────────────────────────────── */
function Accounting({ data }) {
  const s = data.summary;
  return (
    <div className="space-y-6">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-3">
        <KPI icon={IndianRupee} label="Revenue (lifetime)" value={inr(s.revenue_lifetime_inr)} sub={`${s.transactions} transactions`} />
        <KPI icon={TrendingUp}  label="Average transaction" value={inr(s.avg_transaction_inr)} />
        <KPI icon={Users}       label="Currency" value={s.currency} />
      </div>

      <div className="bg-ink-800 border border-ink-700 rounded-xl p-4">
        <p className="text-xs uppercase tracking-wide text-ink-400 mb-3">Per-organization credit balances</p>
        <table className="w-full text-sm">
          <thead><tr className="text-ink-400 text-xs"><th className="text-left py-1">Organization</th><th className="text-right py-1">Quota</th><th className="text-right py-1">Used</th><th className="text-right py-1">Remaining</th></tr></thead>
          <tbody>{data.org_balances.map((o) => (
            <tr key={o.org_id} className="border-t border-ink-700">
              <td className="py-2 text-ink-200">{o.name}</td>
              <td className="py-2 text-right text-ink-400">{o.quota == null ? '—' : num(o.quota)}</td>
              <td className="py-2 text-right text-ink-400">{num(o.used)}</td>
              <td className="py-2 text-right text-ink-100 font-medium">{o.remaining === 'unlimited' ? 'Unlimited' : num(o.remaining)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      <div className="bg-ink-800 border border-ink-700 rounded-xl p-4">
        <p className="text-xs uppercase tracking-wide text-ink-400 mb-3">Recent payments</p>
        {data.recent_payments?.length ? (
          <table className="w-full text-sm">
            <thead><tr className="text-ink-400 text-xs"><th className="text-left py-1">Date</th><th className="text-left py-1">Org</th><th className="text-left py-1">Type</th><th className="text-right py-1">Credits</th><th className="text-right py-1">Amount</th><th className="text-left py-1 pl-2">Status</th></tr></thead>
            <tbody>{data.recent_payments.map((p) => (
              <tr key={p.payment_id} className="border-t border-ink-700">
                <td className="py-2 text-ink-400">{p.created_at ? new Date(p.created_at).toLocaleString() : '—'}</td>
                <td className="py-2 text-ink-400">{p.organization_id}</td>
                <td className="py-2 text-ink-300">{p.type === 'manual_grant' ? 'Grant' : 'Purchase'}</td>
                <td className="py-2 text-right text-ink-100">{p.credits}</td>
                <td className="py-2 text-right text-ink-400">{inr((p.amount || 0) / 100)}</td>
                <td className="py-2 pl-2"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${p.status === 'paid' ? 'bg-green-500/15 text-green-300' : p.status === 'created' ? 'bg-amber-500/15 text-amber-300' : 'bg-red-500/15 text-red-300'}`}>{p.status}</span></td>
              </tr>
            ))}</tbody>
          </table>
        ) : <p className="text-sm text-ink-500">No payments yet.</p>}
      </div>
    </div>
  );
}

/* ── Top-level page ────────────────────────────────────────────────────── */
function MIS() {
  const [tab, setTab] = useState('operations');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async (key) => {
    setLoading(true);
    setError('');
    try {
      const loader = TABS.find((t) => t.key === key).loader;
      const result = await loader();
      setData((d) => ({ ...d, [key]: result }));
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load MIS.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (!data[tab]) load(tab); /* eslint-disable-next-line */ }, [tab]);

  const active = data[tab];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-ink-100 flex items-center gap-2">
          <Activity className="w-5 h-5 text-brand-400" /> MIS
        </h2>
        <p className="text-sm text-ink-300 mt-1">
          Platform-wide management views — what's running, what's earned, what needs attention, and the books.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-ink-700 pb-1">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium ${
              tab === t.key ? 'bg-brand-500 text-white' : 'bg-ink-800 text-ink-300 hover:bg-ink-700'
            }`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
        <button onClick={() => load(tab)} title="Refresh" className="ml-auto text-ink-400 hover:text-ink-100 px-2 py-1.5">
          <Loader className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-300 text-sm flex items-center gap-2">
          <XCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {!active && loading && <p className="text-ink-400 text-sm py-12 text-center">Loading…</p>}
      {active && tab === 'operations' && <Operations data={active} />}
      {active && tab === 'financial'  && <Financial  data={active} />}
      {active && tab === 'support'    && <Support    data={active} />}
      {active && tab === 'accounting' && <Accounting data={active} />}
    </div>
  );
}

export default MIS;
